// Versus surprise drops: weapons, health and mystery items fall into the arena.
//
// The server decides WHEN and WHERE (spot index into the arena's a/g markers) and sends a
// random `pick`; both clients derive the same contents from it. Touching a drop sends a
// 'claim'; the server grants it to the first claimer ('taken'), and only that player gets
// the reward — the other just sees it vanish.

import Phaser from 'phaser';
import { DEPTH } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { GameContext, Vec } from '../../core/contracts';
import { Rng } from '../../core/rng';
import { WEAPON_IDS, type ItemInstance, type OrbType, type WeaponId } from '../../core/types';
import { mechKeys, TEX } from '../../art/keys';
import type { DropMsg } from '../../net/versus';
import { describeItem } from '../loot/describe';
import { makeItem } from '../loot/items';
import { orbShellTex } from '../world/textures';

/** Share of max HP a health drop restores. */
const HEAL_FRACTION = 0.4;
const PICK_RADIUS = 58;
/** Drops fall from above the spot and settle; then bob. */
const FALL_TIME = 0.55;
const LIFETIME = 22;

interface Drop {
  id: number;
  kind: DropMsg['kind'];
  pick: number;
  weapon: WeaponId | null;
  x: number;
  y: number;
  age: number;
  claimed: boolean;
  root: Phaser.GameObjects.Container;
  beam: Phaser.GameObjects.Rectangle;
}

const KIND_COLOR: Record<DropMsg['kind'], number> = {
  weapon: COLORS.volt,
  health: COLORS.heal,
  item: COLORS.orbArsenal,
};

export class VersusDrops {
  private drops = new Map<number, Drop>();
  private spots: Vec[];

  constructor(
    private readonly ctx: GameContext,
    private readonly claim: (id: number) => void,
  ) {
    // Ground spots float a little above the floor; air spots as authored.
    this.spots = [
      ...ctx.world.spawnPoints('air'),
      ...ctx.world.spawnPoints('ground').map((v) => ({ x: v.x, y: v.y - 40 })),
    ];
  }

  spawn(m: DropMsg): void {
    if (this.drops.has(m.id) || this.spots.length === 0) return;
    const scene = this.ctx.scene;
    const spot = this.spots[m.spot % this.spots.length];
    const weapon = m.kind === 'weapon' ? WEAPON_IDS[m.pick % WEAPON_IDS.length] : null;
    const color = KIND_COLOR[m.kind];
    const root = scene.add.container(spot.x, spot.y - 260).setDepth(DEPTH.PICKUPS);
    const halo = scene.add.image(0, 0, TEX.soft).setTint(color).setBlendMode(Phaser.BlendModes.ADD).setDisplaySize(130, 130).setAlpha(0.55);
    let icon: Phaser.GameObjects.Image;
    if (m.kind === 'weapon' && weapon && scene.textures.exists(mechKeys.weapon(weapon))) {
      icon = scene.add.image(0, 0, mechKeys.weapon(weapon));
      icon.setScale(Math.min(1.6, 62 / Math.max(1, icon.width)));
    } else if (m.kind === 'health' && scene.textures.exists(TEX.health)) {
      icon = scene.add.image(0, 0, TEX.health).setScale(1.3);
    } else {
      const types: OrbType[] = ['arsenal', 'armor', 'unstable'];
      icon = scene.add.image(0, 0, orbShellTex(scene, types[m.pick % types.length])).setScale(0.9);
    }
    const ring = scene.add.circle(0, 0, 30).setStrokeStyle(2, color, 0.9).setFillStyle(0, 0);
    root.add([halo, ring, icon]);
    // A light beam marks where it lands while it falls.
    const beam = scene.add
      .rectangle(spot.x, spot.y - 400, 26, 800, color, 0.12)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(DEPTH.WORLD_BACK + 5);
    this.drops.set(m.id, { id: m.id, kind: m.kind, pick: m.pick, weapon, x: spot.x, y: spot.y, age: 0, claimed: false, root, beam });
    try {
      this.ctx.audio.play('loot_reveal', { volume: 0.55 });
    } catch {
      // Optional sound.
    }
  }

  /** Drops as the AI pilot sees them. */
  list(): { id: number; kind: DropMsg['kind']; x: number; y: number; landed: boolean }[] {
    return [...this.drops.values()].map((d) => ({ id: d.id, kind: d.kind, x: d.x, y: d.y, landed: d.age >= FALL_TIME }));
  }

  /** Server granted drop `id` to `mine` (true = the local player). Returns what it was. */
  taken(id: number, mine: boolean): { kind: DropMsg['kind']; weapon: WeaponId | null } | null {
    const d = this.drops.get(id);
    if (!d) return null;
    this.drops.delete(id);
    const { fx } = this.ctx;
    try {
      fx.ring(d.x, d.y, 70, { color: KIND_COLOR[d.kind], width: 3, fromRadius: 20 });
      fx.sparks(d.x, d.y, -Math.PI / 2, { count: 10, color: KIND_COLOR[d.kind], speed: 300, spread: Math.PI * 2 });
    } catch {
      // Cosmetic only.
    }
    d.root.destroy();
    d.beam.destroy();
    if (mine) this.grant(d);
    return { kind: d.kind, weapon: d.weapon };
  }

  private grant(d: Drop): void {
    const { ctx } = this;
    const p = ctx.player;
    const rng = new Rng(d.pick);
    let label = '';
    if (d.kind === 'health') {
      p.heal(p.stats.maxHp * HEAL_FRACTION);
      label = `+${Math.round(HEAL_FRACTION * 100)}% HULL`;
    } else {
      let item: ItemInstance | null = null;
      if (d.kind === 'weapon' && d.weapon) {
        item = makeItem(d.weapon, rng.chance(0.3) ? 'advanced' : 'standard', rng);
      } else {
        const types: OrbType[] = ['arsenal', 'armor', 'unstable'];
        try {
          item = ctx.loot.roll(types[d.pick % types.length], 'crate');
        } catch {
          item = null;
        }
      }
      if (item) {
        const inv = ctx.run.inventory;
        const replaced = inv.equip(item);
        inv.changed();
        p.refreshLoadout();
        label = describeItem(item).name.toUpperCase();
        void replaced; // Swapped-out gear is simply gone in versus (keeps the arena clean).
      }
    }
    try {
      ctx.audio.play(d.kind === 'health' ? 'heal' : 'equip', { volume: 0.8 });
    } catch {
      // Cosmetic only.
    }
    if (label) ctx.bus.emit('ui:toast', { text: label, color: KIND_COLOR[d.kind] });
  }

  update(dt: number): void {
    const p = this.ctx.player;
    for (const d of this.drops.values()) {
      d.age += dt;
      const fall = Math.min(1, d.age / FALL_TIME);
      const ease = 1 - (1 - fall) * (1 - fall);
      const bob = fall >= 1 ? Math.sin(d.age * 3) * 5 : 0;
      d.root.setPosition(d.x, d.y - 260 * (1 - ease) + bob);
      d.root.setAngle(fall < 1 ? 0 : Math.sin(d.age * 2) * 4);
      d.beam.setAlpha(fall < 1 ? 0.18 : Math.max(0, 0.12 - (d.age - FALL_TIME) * 0.08));
      if (d.age > LIFETIME) d.root.setAlpha(Math.max(0, 1 - (d.age - LIFETIME)));
      if (fall < 1 || d.claimed || !p.alive) continue;
      if (Math.hypot(p.x - d.x, p.y - d.y) <= PICK_RADIUS) {
        d.claimed = true;
        this.claim(d.id);
      }
    }
  }

  destroy(): void {
    for (const d of this.drops.values()) {
      d.root.destroy();
      d.beam.destroy();
    }
    this.drops.clear();
  }
}
